/* One shimmer definition, inherited by every placeholder shape.
   `background-size: 200%` with a moving position is a compositor-friendly
   sweep — no layout, no paint of anything but the gradient. */
.line,
.bar,
.poster {
  display: block;
  border-radius: var(--radius-sm);
  background: linear-gradient(
    90deg,
    var(--bg-elevated) 0%,
    var(--bg-active) 40%,
    var(--bg-elevated) 80%
  );
  background-size: 200% 100%;
  animation: sweep 1.4s ease-in-out infinite;
}

@keyframes sweep {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -50% 0;
  }
}

/* The shimmer conveys "loading", which the status text also says. Motion here
   is reinforcement, not information. */
@media (prefers-reduced-motion: reduce) {
  .line,
  .bar,
  .poster,
  .ghost,
  .pulse {
    animation: none !important;
  }
}

.line {
  height: 12px;
}

.line[data-h='short'] {
  height: 9px;
}

.line[data-h='tall'] {
  height: 22px;
}

.line[data-w='14'] { width: 14%; }
.line[data-w='18'] { width: 18%; }
.line[data-w='30'] { width: 30%; }
.line[data-w='34'] { width: 34%; }
.line[data-w='40'] { width: 40%; }
.line[data-w='42'] { width: 42%; }
.line[data-w='48'] { width: 48%; }
.line[data-w='54'] { width: 54%; }
.line[data-w='55'] { width: 55%; }
.line[data-w='60'] { width: 60%; }
.line[data-w='80'] { width: 80%; }
.line[data-w='88'] { width: 88%; }
.line[data-w='96'] { width: 96%; }

/* ── Per-type shapes ──────────────────────────────────────── */

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  min-height: 160px;
}

.poster {
  width: 92px;
  height: 138px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
}

.bannerText {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
  min-width: 0;
}

.kpis {
  display: flex;
  gap: var(--space-6);
  flex-wrap: wrap;
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 120px;
  flex: 1;
}

.chart {
  display: flex;
  align-items: flex-end;
  gap: var(--space-3);
  height: 180px;
  padding-top: var(--space-2);
}

.bar {
  flex: 1;
  border-radius: var(--radius-sm) var(--radius-sm) 2px 2px;
}

.table {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.prose {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* ── Whole-document placeholder ───────────────────────────── */

.doc {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-width: 1100px;
}

.status {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-secondary);
}

.pulse {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--ai-accent);
  animation: breathe 1.6s ease-in-out infinite;
}

@keyframes breathe {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

.docGrid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: var(--space-4);
  align-items: start;
}

.ghost {
  padding: var(--space-5);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  animation: riseIn var(--duration-base) var(--ease-default) both;
}

.ghost[data-layout='full'] {
  grid-column: span 12;
}

.ghost[data-layout='half'] {
  grid-column: span 6;
}

@media (max-width: 900px) {
  .ghost[data-layout='half'] {
    grid-column: span 12;
  }
}

.ghostHead {
  display: block;
  margin-bottom: var(--space-4);
}

@keyframes riseIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
